/**
 * React bindings for Telegram's native chrome.
 *
 * Callbacks live in refs so a re-render with a fresh inline closure does not detach and
 * re-attach the native listener — Telegram's onClick/offClick pair is identity-based, and
 * churning it drops taps.
 */

import { useEffect, useRef } from 'react';
import { haptic, hapticNotify, hapticSelection, supports, syncChrome, twa } from '../lib/telegram';

export { haptic, hapticNotify, hapticSelection };

/** Telegram's own back arrow in the header. Shown while mounted, hidden on unmount. */
export function useBackButton(onBack: (() => void) | null) {
  const cb = useRef(onBack);
  cb.current = onBack;
  const enabled = onBack !== null;

  useEffect(() => {
    const w = twa();
    if (!w || !enabled) return;
    const handler = () => cb.current?.();
    w.BackButton.onClick(handler);
    w.BackButton.show();
    return () => {
      w.BackButton.offClick(handler);
      w.BackButton.hide();
    };
  }, [enabled]);
}

export interface MainButtonOptions {
  text: string;
  onClick: () => void;
  /** Hide entirely — e.g. once a success placeholder has replaced the form. */
  visible?: boolean;
  disabled?: boolean;
  progress?: boolean;
  /** Override the fill; used for the transient "Copied ✓" success state. */
  color?: string;
  textColor?: string;
}

/** Telegram's BottomButton: the one primary action for the current screen. */
export function useMainButton(opts: MainButtonOptions | null) {
  const cb = useRef(opts?.onClick);
  cb.current = opts?.onClick;

  const visible = opts ? opts.visible !== false : false;
  const text = opts?.text;
  const disabled = opts?.disabled;
  const progress = opts?.progress;
  const color = opts?.color;
  const textColor = opts?.textColor;

  useEffect(() => {
    const w = twa();
    if (!w) return;
    const handler = () => cb.current?.();
    w.MainButton.onClick(handler);
    return () => {
      w.MainButton.offClick(handler);
      w.MainButton.hide();
    };
  }, []);

  useEffect(() => {
    const w = twa();
    if (!w) return;
    const btn = w.MainButton;
    if (!visible || !text) {
      btn.hide();
      return;
    }
    btn.setText(text);
    if (color || textColor) {
      try {
        btn.setParams?.({
          ...(color ? { color } : {}),
          ...(textColor ? { text_color: textColor } : {}),
        });
      } catch {
        /* pre-6.10 clients have no setParams */
      }
    }
    if (disabled) btn.disable();
    else btn.enable();
    if (progress) btn.showProgress(true);
    else btn.hideProgress();
    btn.show();
  }, [visible, text, disabled, progress, color, textColor]);
}

/**
 * Keep Telegram's chrome in step with the screen underneath, and re-apply on theme change so
 * a mid-session switch to dark does not leave a white header stranded above a dark app.
 */
export function useChrome(
  opts: { header?: 'surface' | 'page'; bottomBar?: 'surface' | 'page' } = {},
) {
  const header = opts.header ?? 'surface';
  const bottomBar = opts.bottomBar ?? 'surface';

  useEffect(() => {
    syncChrome({ header, bottomBar });
    const w = twa();
    if (!w) return;
    const onTheme = () => syncChrome({ header, bottomBar });
    w.onEvent('themeChanged', onTheme);
    return () => w.offEvent('themeChanged', onTheme);
  }, [header, bottomBar]);
}

/** Stop Telegram closing the app when the user swipes down inside a scrollable screen. */
export function useDisableVerticalSwipes(active = true) {
  useEffect(() => {
    const w = twa();
    if (!w || !supports('7.7') || !active) return;
    w.disableVerticalSwipes?.();
    return () => w.enableVerticalSwipes?.();
  }, [active]);
}
